import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Receipt, ArrowRight } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { supabase } from '../lib/supabase';
import { paisFlags } from '../lib/checkoutPlans';
import AddToHomescreenPrompt from '../components/AddToHomescreenPrompt';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [nameShort, setNameShort] = useState<string>('');
  const [isMale, setIsMale] = useState<boolean>(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const detection = useDeviceDetection();
  const [searchParams] = useSearchParams();
  const [isArgentina, setIsArgentina] = useState(false);
  // ?preview fuerza el cartelito de facturación aunque la cuenta no sea AR (para revisar diseño).
  const preview = searchParams.get('preview') !== null;

  const handleContinue = useCallback(() => {
    if (detection.canInstall && !detection.isStandalone) {
      setShowInstallPrompt(true);
    } else {
      navigate('/');
    }
  }, [detection.canInstall, detection.isStandalone, navigate]);

  const handleInstallDismiss = useCallback(() => {
    setShowInstallPrompt(false);
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('name_short, name_sex_male, id_company')
          .eq('id', user.id)
          .single();
        if (profile) {
          setNameShort((profile as any).name_short || '');
          const male = (profile as any)?.name_sex_male === null ? true : !!(profile as any)?.name_sex_male;
          setIsMale(male);
          // Solo Argentina puede facturar por AFIP → mostramos el opt-in de facturación.
          const idc = (profile as any)?.id_company as string | null | undefined;
          if (idc) {
            const { data: company } = await supabase
              .from('company_profiles')
              .select('pais')
              .eq('id_company', idc)
              .maybeSingle();
            setIsArgentina(paisFlags((company as { pais?: string | null } | null)?.pais).isArgentina);
          }
        }
      } catch {}
    })();
  }, []);

  return (
    <div className="fixed inset-0 z-40 md:absolute md:inset-0 md:z-auto w-full md:flex md:items-center md:justify-center bg-surface-card md:bg-transparent overflow-y-auto">
      <div className="relative bg-surface-card w-full min-h-full md:min-h-0 md:h-auto px-4 md:px-8 py-10 pb-28 md:py-12 md:max-w-4xl md:mx-auto md:rounded-lg md:border md:border-neutral-200 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto mb-4 w-24 h-24">
            <DotLottieReact
              src="/images/lottie_animations/Success.lottie"
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <h1 className="text-[17px] font-semibold text-brand-black/90 mb-2">Subscripción exitosa.</h1>
          <p className="text-[15px] text-neutral-600">
            {`${nameShort ? `${nameShort}, ` : ''}${isMale ? 'bienvenido' : 'bienvenida'} a la revolución de la IA.`}
          </p>

          {/* Opt-in de facturación AFIP — solo Argentina (o ?preview). Es a demanda:
              lo carga una vez y de ahí sus facturas salen solas. Ver ADR-2026-08-03-facturacion-*. */}
          {(isArgentina || preview) && (
            <div className="mt-6 rounded-xl border border-neutral-200 bg-surface-bg p-4 text-left">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-neutral-100 text-brand-primary flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-brand-black/90">¿Querés la factura de tu plan?</p>
                  <p className="text-[13px] text-neutral-500 mt-0.5 leading-snug">
                    Cargá tus datos fiscales una vez y te generamos la factura AFIP de este pago y de los próximos. Podés descontar el IVA.
                  </p>
                  <button
                    onClick={() => navigate('/facturacion')}
                    className="inline-flex items-center gap-1.5 mt-3 h-9 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13.5px] font-medium transition-colors"
                  >
                    Configurar facturación <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desktop inline helper + button */}
          <div className="hidden md:block mt-8">
            <p className="text-[13px] text-neutral-400 mb-3">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de Ayuda.</p>
            <button
              onClick={handleContinue}
              className="inline-flex items-center justify-center h-10 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
      {/* Mobile fixed bottom actions. El fondo blanco de la barra debe pintar a
          través de env(safe-area-inset-bottom) (zona del home indicator en PWA
          standalone / iOS con notch); sin esto asoma el #F2F2F7 del <html>. */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-surface-card px-4 pt-4 border-t border-neutral-200"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <p className="text-[13px] text-neutral-400 mb-2 text-center">Ante cualquier duda te podes comunicar con nostros yendo a la pestaña de ayuda.</p>
        <button
          onClick={handleContinue}
          className="w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          Continuar
        </button>
      </div>
      {showInstallPrompt && (
        <AddToHomescreenPrompt
          appName="Atlas"
          appIconUrl="/favicon.png"
          delayMs={0}
          forceShow
          origen="suscripcion_exitosa"
          onDismiss={handleInstallDismiss}
          onInstall={handleInstallDismiss}
        />
      )}
    </div>
  );
};

export default SubscriptionSuccess;
